import { Music } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import type { SongListItemDTO } from '../../../shared/dto/library';
import { Card, EmptyState } from '../../components/ui';
import { useT } from '../../i18n';
import { groupByLetter, sortByTitle } from './collate';
import { SongRow } from './SongRow';

/** All songs A–Ö (Icelandic order) with sticky index letters. */
export function AllSongsList({
  songs,
  songHref,
  emptyAction,
  showCounts,
  rowAction,
}: {
  songs: SongListItemDTO[];
  songHref: (id: string) => string;
  emptyAction?: ReactNode;
  showCounts?: boolean;
  /** Optional per-row action (e.g. "Vista fyrir ónettengt"). */
  rowAction?: (song: SongListItemDTO) => ReactNode;
}) {
  const t = useT();
  const groups = useMemo(() => groupByLetter(sortByTitle(songs)), [songs]);
  if (songs.length === 0) {
    return (
      <EmptyState icon={Music} title={t('library.empty_songs')} action={emptyAction}>
        {t('library.empty_songs_hint')}
      </EmptyState>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {groups.map((g) => (
        <section key={g.letter} aria-label={g.letter}>
          <h2
            className="sticky z-10 -mx-1 bg-[var(--bg)]/95 px-2 py-1 text-[17px] font-bold text-[var(--accent)] backdrop-blur"
            style={{ top: 'calc(var(--top-inset) + 4rem + 1px)' }}
          >
            {g.letter}
          </h2>
          <Card className="overflow-hidden">
            <ul className="divide-y divide-[var(--line)]">
              {g.items.map((s) => (
                <SongRow key={s.id} song={s} to={songHref(s.id)} showCounts={showCounts} action={rowAction?.(s)} />
              ))}
            </ul>
          </Card>
        </section>
      ))}
    </div>
  );
}
